import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { injectIntl } from 'react-intl';
import { useMutation } from '@apollo/react-hooks';
import ManageEnforced from './ManageEnforced';
import { withAccount } from '../../../../components/AccountContext';
import ManageMandatory from './ManageMandatory';
import ManageDelete from './ManageDelete';
import EditQuestionDialog from './EditQuestionDialog';
import messages from '../intl-messages';
import { UPDATE_GLOBAL_ARI_RANK } from '../mutations';
import { withGrowl } from '../../../../components/GrowlNotifications';
import EditAnswersDialog from './EditAnswersDialog';
import ManageAllowUseForSuperLinks from './ManageAllowUseForSuperLinks';
import ManageAllowUseForQuestionnaires from './ManageAllowUseForQuestionnaires';


const ManageGlobalARIFields = ({
  currentAccount, data, intl: { formatMessage }, growl,
}) => {
  const [tableData, setTableData] = useState([]);
  const [globalAriFields, setGlobalAriFields] = useState([]);
  const [editQuestionDialogIsVisible, setEditQuestionDialogIsVisible] = useState(false);
  const [editQuestionDialogFieldData, setEditQuestionDialogFieldData] = useState(null);
  const [editAnswersDialogIsVisible, setEditAnswersDialogIsVisible] = useState(false);
  const [editAnswersDialogFieldData, setEditAnswersDialogFieldData] = useState();
  const [changingRank, setChangingRank] = useState(false);

  const [updateGlobalAriRank] = useMutation(UPDATE_GLOBAL_ARI_RANK, { refetchQueries: ['getAccount'] });

  useEffect(() => {
    if (data) {
      setGlobalAriFields(
        _.sortBy(data.account.globalAriFields, f => f.rank)
          .map(field => ({
            id: field.id,
            rank: field.rank,
            question: field.question,
            dataType: field.dataType,
            mandatory: field.mandatory,
            enabled: field.enabled,
            answers: field.answers,
            accountsWithFieldEnabled: field.accountsWithFieldEnabled,
            availableAccountsToEnable: field.availableAccountsToEnable,
          })),
      );

      setTableData(
        _.sortBy(data.account.globalAriFields, f => f.rank)
          .map(field => ({
            id: field.id,
            rank: field.rank,
            question: field.question,
            type: field.dataType,
          })),
      );
    }
  }, [data]);

  const questionTemplate = (rowData) => {
    const field = _.find(globalAriFields, global => global.id === rowData.id);
    return (
      <div>
        <div className="p-col-12">
          {rowData.question}
        </div>

        <div className="p-col-12">
          <Button
            icon="pi pi-angle-double-up"
            className="p-button-secondary"
            disabled={changingRank || rowData.rank === 1}
            onClick={() => {
              setChangingRank(true);

              const input = {
                accountId: currentAccount,
                ariFieldId: rowData.id,
                operation: 'DECREASE',
              };

              updateGlobalAriRank({ variables: { input } })
                .then(() => {
                  growl.show({
                    severity: 'success',
                    sticky: false,
                    summary: formatMessage(messages.growlAddFieldSuccessSummary),
                    detail: formatMessage(messages.growlRankChangedSuccessDetail),
                  });
                  setChangingRank(false);
                })
                .catch(() => {
                  growl.show({
                    severity: 'error',
                    sticky: true,
                    summary: formatMessage(messages.growlAddFieldFailSummary),
                    detail: formatMessage(messages.growlRankChangedFailDetail),
                  });
                  setChangingRank(false);
                });
            }}
          />

          <Button
            className="p-button-secondary"
            label="..."
            onClick={() => {
              setEditQuestionDialogFieldData(field);
              setEditQuestionDialogIsVisible(true);
            }}
          />

          <Button
            icon="pi pi-angle-double-down"
            className="p-button-secondary"
            disabled={changingRank || rowData.rank === globalAriFields.length}
            onClick={() => {
              setChangingRank(true);

              const input = {
                accountId: currentAccount,
                ariFieldId: rowData.id,
                operation: 'INCREASE',
              };

              updateGlobalAriRank({ variables: { input } })
                .then(() => {
                  growl.show({
                    severity: 'success',
                    sticky: false,
                    summary: formatMessage(messages.growlAddFieldSuccessSummary),
                    detail: formatMessage(messages.growlRankChangedSuccessDetail),
                  });
                  setChangingRank(false);
                })
                .catch(() => {
                  growl.show({
                    severity: 'error',
                    sticky: true,
                    summary: formatMessage(messages.growlAddFieldFailSummary),
                    detail: formatMessage(messages.growlRankChangedFailDetail),
                  });
                  setChangingRank(false);
                });
            }}
          />
        </div>
      </div>
    );
  };

  const typeTemplate = (rowData) => {
    const field = _.find(globalAriFields, global => global.id === rowData.id);
    return (
      <div>
        {rowData.type}
        {
          (field.dataType === 'DROPDOWN') && (
            <>
              <div style={{ fontStyle: 'italic' }}>
                {`${field.answers.length} ${formatMessage(messages.answersLabel)}`}
              </div>
              <Button
                label="..."
                className="p-button-secondary"
                onClick={() => {
                  setEditAnswersDialogFieldData(field);
                  setEditAnswersDialogIsVisible(true);
                }}
              />
            </>
          )
        }
      </div>
    );
  };

  const optionsTemplate = (rowData) => {
    const field = _.find(globalAriFields, global => global.id === rowData.id);

    return (
      <div>
        <div className="p-col-12" style={{ margin: '0px', padding: '0px' }}>
          <ManageMandatory
            field={field}
            currentAccount={currentAccount}
          />
        </div>
        <div className="p-col-12" style={{ margin: '0px', padding: '0px' }}>
          <ManageEnforced
            field={field}
            currentAccount={currentAccount}
          />
        </div>
      </div>
    );
  };

  const deleteTemplate = (rowData) => {
    const field = _.find(globalAriFields, global => global.id === rowData.id);

    return (
      <div className="p-col-12" style={{ margin: '0px', padding: '0px' }}>
        <ManageDelete
          field={field}
          currentAccount={currentAccount}
        />
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '10px', width: '100%' }}>
      <Panel
        className="p-col-12 sub-panel"
        header={formatMessage(messages.globalManagePanelHeader)}
      >
        <div className="p-grid">
          <div className="p-col-12" style={{ textAlign: 'center' }}>
            <ManageAllowUseForSuperLinks
              data={data}
              currentAccount={currentAccount}
            />
          </div>
          <div className="p-col-12" style={{ textAlign: 'center' }}>
            <ManageAllowUseForQuestionnaires
              data={data}
              currentAccount={currentAccount}
            />
          </div>
          {globalAriFields.length === 0
            ? (
              <div style={{ width: '100%', textAlign: 'center' }}>
                <small>
                  {formatMessage(messages.globalManageNoFields)}
                </small>
              </div>
            )
            : (
              <>
                <DataTable
                  responsive
                  value={tableData}
                >
                  <Column
                    field="question"
                    header={formatMessage(messages.questionLabel)}
                    body={questionTemplate}
                    style={{ textAlign: 'center', width: '10em', fontStyle: 'italic' }}
                  />
                  <Column
                    field="type"
                    header={formatMessage(messages.typeLabel)}
                    body={typeTemplate}
                    style={{ textAlign: 'center', width: '8em' }}
                  />
                  <Column
                    body={optionsTemplate}
                    style={{ textAlign: 'center', width: '8em' }}
                  />
                  <Column
                    body={deleteTemplate}
                    style={{ textAlign: 'center', width: '6em' }}
                  />
                </DataTable>

                {editQuestionDialogFieldData && (
                  <EditQuestionDialog
                    isVisible={editQuestionDialogIsVisible}
                    onHide={() => {
                      setEditQuestionDialogFieldData(null);
                      setEditQuestionDialogIsVisible(false);
                    }}
                    field={editQuestionDialogFieldData}
                    currentAccount={currentAccount}
                  />
                )}

                {editAnswersDialogFieldData && (
                  <EditAnswersDialog
                    isVisible={editAnswersDialogIsVisible}
                    onHide={() => {
                      setEditAnswersDialogFieldData(null);
                      setEditAnswersDialogIsVisible(false);
                    }}
                    field={editAnswersDialogFieldData}
                    currentAccount={currentAccount}
                  />
                )
                }
              </>
            )}
        </div>
      </Panel>
    </div>
  );
};

ManageGlobalARIFields.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withAccount(injectIntl(withGrowl(ManageGlobalARIFields)));
